import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createStyles, Dialog as MuiDialog, DialogContent, Theme, withStyles } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import { DialogTitle, DialogTitleComponentProps, DialogTitleProps } from './DialogTitle';
import { DialogActions, IDialogActionsProps } from './DialogActions';
import { ResizeCallbackData } from 'react-resizable';
import { useWindowSize } from 'hooks/useWindowSize';
import { debounce } from 'debounce';
import { DEFAULT_SIZES, DIALOG_MARGIN } from './DialogConstant';
import { ControlPosition, DraggableEventHandler } from 'react-draggable';
import { generateGuid } from 'utils/Guid';
import { ensureTabManagement, TabManagementUtil } from 'utils/TabManagementUtil';
import { DialogPosition, positionDialog, useStyles } from './DialogUtil';
import { DialogResizableBox, IDialogResizableBoxProps } from './DialogResizableBox';
import { DialogPaperComponent } from './DialogPaperComponent';
import { useMaxZIndex } from 'hooks/useMaxZIndex';
import { mergeRefs } from 'utils/Refs';
import { useWindowResize } from 'hooks/useWindowResize';

/**
 * Need to override the sizes and make it to accept only number
 * As react-resizable supports sizes in number only
 */
export interface DialogCssProperties extends CSSProperties {
  height?: number;
  width?: number;
  minHeight?: number;
  minWidth?: number;
  maxHeight?: number;
  maxWidth?: number;
}

interface DialogCallbacks {
  /**After dialog close */
  onClose?: () => any;
  /**When dialog overlay clicked */
  onBackdropClick?: () => any;
  /**When dialog resizing stops */
  onResizeStop?: (e: React.SyntheticEvent, data: ResizeCallbackData) => any;
  /**When dialog resizing starts */
  onResizeStart?: (e: React.SyntheticEvent, data: ResizeCallbackData) => any;
  /**While resizing dialog */
  onResize?: (e?: React.SyntheticEvent, data?: ResizeCallbackData) => any;
  /** When dialog drag starts. */
  onDragStart?: DraggableEventHandler;
  /** While dragging dialog */
  onDrag?: DraggableEventHandler;
  /** When dialog dragging stops */
  onDragStop?: DraggableEventHandler;
  /** Fired after the dialog enters.*/
  onAfterOpen?: (node: HTMLElement, isAppearing: boolean) => any;
  /**Fired before the dialog enters. */
  onBeforeOpen?: (node: HTMLElement, isAppearing: boolean) => any;
  /**Fired when the dialog is entering. */
  onOpening?: (node: HTMLElement, isAppearing: boolean) => any;
  /**Before dialog closes */
  onBeforeClose?: (node: HTMLElement) => void;
}

interface KeyboardOptions {
  /**Auto focus element with the least tabindex */
  autoFocusFirst?: boolean;
  /**Trap tabbing within the dialog */
  trapTabbing?: boolean;
  /**Close dialog on the Esc key press */
  closeOnEscapeKey?: boolean;
}

export interface DialogProps extends DialogCallbacks {
  open: boolean;
  /** CSS class for conent holder element (between header and footer, inside dialog element) */
  holderClassName?: string;
  styles?: DialogCssProperties;
  /** Class for element of the dialog itself */
  className?: string;
  dialogTitle?: DialogTitleProps;
  dialogActions?: IDialogActionsProps;
  isFullScreen?: boolean;
  /**
   * Remove resizing feature make it the same height as its content. **Defaults `false`**
   * This is also exclusive of fullscreen / toggling full screen.
   *
   * _**Warning:** Changing this while a dialog is showing will cause children to un-mount & remount, due to "re-parenting."_
   */
  isResponsive?: boolean;
  isResizeDisabled?: boolean;
  closeOnBackdropClick?: boolean;
  handleWindowResize?: boolean;
  /**Remove full screen action from header */
  disableFullScreenAction?: boolean;
  /**Horizontal divider between header,content and footer */
  showDividers?: boolean;
  /**Ignores the default title view and uses this */
  titleComponent?: React.ReactNode;
  /**Ignores the default action view and uses this */
  actionComponent?: React.ReactNode;
  contentClassName?: string;
  contentStyles?: React.CSSProperties;
  /**Override default center position with specified custom position */
  dialogPosition?: DialogPosition;
  keyboard?: KeyboardOptions;
  hideTitle?: boolean;
  components?: {
    dialogBox?: React.FC<IDialogResizableBoxProps>;
    title?: React.FC<DialogTitleComponentProps>;
  };
  contentRef?: React.MutableRefObject<HTMLDivElement | undefined> | React.LegacyRef<HTMLDivElement | undefined>;
}

const dialogStyles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    },
  });

const DpDialog = React.forwardRef((props: React.PropsWithChildren<DialogProps>, ref) => {
  const {
    onDrag,
    onDragStart,
    onDragStop,
    styles,
    components,
    contentRef,
    handleWindowResize: handleWindowResizeProps,
  } = props;
  const DialogBox = components?.dialogBox ?? DialogResizableBox;
  const Title = components?.title ? components.title : DialogTitle;
  const [fullScreen, setFullScreen] = useState(props.isFullScreen);
  const { zIndex } = useMaxZIndex();
  const classes = useStyles({
    isResponsive: props.isResponsive,
    styles: props.styles,
    isFullScreen: fullScreen,
    dialogPosition: props.dialogPosition,
  });
  const dialogPaperClasses = classNames(
    'dp-dialog dp-dialog-paper',
    props.className,
    classes.dialogPaper,
    classes.dialogPosition,
    {
      [classes.dialogPaperSize]: !fullScreen,
      'dp-dialog--full-screen': fullScreen,
    }
  );
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const draggableId = useRef(`dp-dailog-${generateGuid()}`);
  const paperRef = useRef<HTMLDivElement>();
  const prevResizeData = useRef<ResizeCallbackData>();
  const dragPosition = useRef<ControlPosition>();
  const propsPositionX = props.dialogPosition?.left;
  const propsPositionY = props.dialogPosition?.top;
  const propsWidth = styles?.width;
  const propsHeight = styles?.height;
  const propsMinWidth = styles?.minWidth ?? 0;
  const propsMinHeight = styles?.minHeight ?? 0;
  const [size, setSize] = useState({
    width: styles && styles.width ? styles.width : DEFAULT_SIZES,
    height: styles && styles.height ? styles.height : DEFAULT_SIZES,
  });
  const dialogMargin: number = typeof props.styles?.margin === 'number' ? props.styles.margin : DIALOG_MARGIN;
  const dialogContentRef = useRef<HTMLDivElement>();
  const tabManager = useRef<TabManagementUtil>();

  const resizeDialog = useCallback(
    (width: number, height: number) => {
      if (paperRef && paperRef.current) {
        // keep it inside the viewport, and track the x,y of where it ended up
        width = Math.max(propsMinWidth, Math.min(width, windowWidth - dialogMargin * 2));
        height = Math.max(propsMinHeight, Math.min(height, windowHeight - dialogMargin * 2));
        setSize({
          height,
          width,
        });
        // reset position to center after changing width and height outside
        positionDialog(
          {
            data: {
              size: {
                width,
                height,
              },
            } as any, // other parameters not required for resizing
            // inline position (transform) need to be reset when we handling position from outside
            fixed: propsPositionX && propsPositionY ? { x: 0, y: 0 } : undefined,
            paperElement: paperRef.current,
            windowHeight,
            windowWidth,
          },
          dialogMargin
        );
      }
    },
    [dialogMargin, propsMinHeight, propsMinWidth, propsPositionX, propsPositionY, windowHeight, windowWidth]
  );

  useEffect(() => {
    const width = propsWidth ? propsWidth : DEFAULT_SIZES;
    const height = propsHeight ? propsHeight : DEFAULT_SIZES;
    resizeDialog(width, height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsHeight, propsWidth, propsPositionX, propsPositionY]);

  const handleEntered = useCallback(
    (node: HTMLElement, isAppearing: boolean) => {
      if (props.keyboard && props.open && dialogContentRef.current) {
        //trap the tabbing inside the dialog
        tabManager.current = ensureTabManagement(
          dialogContentRef.current,
          props.keyboard.autoFocusFirst,
          props.keyboard.trapTabbing
        );
        // either focus first element, or cancel it, depending on autoFocusFirst value.
        // necessary to call this programmatically since we are reusing a util, which is preferred.
        tabManager.current.focusFirstElement(props.keyboard.autoFocusFirst);
      }
      props.onAfterOpen && props.onAfterOpen(node, isAppearing);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.keyboard, props.open, props.onAfterOpen]
  );

  const handleClose = useCallback(() => {
    tabManager.current?.cleanupInstance();
    props.onClose && props.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onClose]);

  const handleFullScreen = useCallback(() => {
    if (props.disableFullScreenAction) return;

    let nowFullScreen = false;
    setFullScreen(pre => {
      nowFullScreen = !pre;
      return nowFullScreen;
    });

    if (nowFullScreen) {
      // make sure it's back to 0,0 wherever it was dragged
      positionDialog({
        fixed: { x: 0, y: 0 },
        paperElement: paperRef.current,
        windowHeight,
        windowWidth,
      });
    } else {
      // put it back to previously set size:
      prevResizeData.current && setSize(prevResizeData.current.size);

      // put it back to where it was last dragged to
      positionDialog({
        fixed: dragPosition.current,
        paperElement: paperRef.current,
        windowHeight,
        windowWidth,
      });
    }
  }, [props.disableFullScreenAction, windowHeight, windowWidth]);

  /** Make sure dialog does not get outside of viewport */
  const handleResize = useCallback(
    (e: SyntheticEvent, data: ResizeCallbackData) => {
      if (paperRef && paperRef.current) {
        setSize({
          height: data.size.height,
          width: data.size.width,
        });
        // keep it inside the viewport, and track the x,y of where it ended up
        dragPosition.current = positionDialog(
          {
            data,
            paperElement: paperRef.current,
            windowHeight,
            windowWidth,
          },
          dialogMargin
        );
      }
      props.onResize && props.onResize(e, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onResize, windowHeight, windowWidth]
  );

  /** Make sure dialog does not get outside of viewport */
  const handleResizeStop = useCallback(
    (e: SyntheticEvent, data: ResizeCallbackData) => {
      if (paperRef && paperRef.current) {
        // keep track of previous drag position, so we can put it back if returning from fullscreen:
        prevResizeData.current = data;
      }
      props.onResizeStop && props.onResizeStop(e, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onResize, windowHeight, windowWidth]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      if (props.dialogActions?.actions) {
        const actions = props.dialogActions?.actions;

        for (let action of actions) {
          if (e.key === action.key) {
            action.onClick?.(e);
          }
        }
      }
      if (e.key === 'Escape' && props.keyboard?.closeOnEscapeKey) {
        handleClose();
      }
    },
    [handleClose, props.dialogActions?.actions, props.keyboard?.closeOnEscapeKey]
  );

  const handleWindowResize = useCallback(
    () => {
      if (!handleWindowResizeProps) {
        return;
      }
      resizeDialog(size.width, size.height);

      props.onResize && props.onResize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogMargin, size.height, size.width, windowHeight, windowWidth]
  );

  const handleWindowResizeDebounced = useMemo(() => debounce(handleWindowResize, 200), [handleWindowResize]);

  useWindowResize(handleWindowResizeDebounced);

  const maxAvailableHeight = windowHeight - dialogMargin * 2;
  const maxAvailableWidth = windowWidth - dialogMargin * 2;
  const height = Math.max(propsMinHeight, Math.min(size.height, maxAvailableHeight));
  const width = Math.max(propsMinWidth, Math.min(size.width, maxAvailableWidth));

  return (
    <MuiDialog
      classes={{ paper: dialogPaperClasses }}
      style={{ zIndex }}
      onClose={handleClose}
      className={classNames('dp-dialog-holder', props.holderClassName, 'ui-front')}
      PaperComponent={DialogPaperComponent as any} // always left draggable to avoid un-mount+remount of child components & data loss.
      aria-labelledby="draggable-dialog-title"
      open={props.open}
      fullScreen={fullScreen}
      maxWidth={false}
      ref={mergeRefs([paperRef, ref])}
      onBackdropClick={props.onBackdropClick}
      disableBackdropClick={!props.closeOnBackdropClick}
      PaperProps={{ draggableId, dragPosition, onDragStart, onDrag, onDragStop } as any} // pass local control props to the paper component.
      onEnter={props.onBeforeOpen}
      onEntered={handleEntered}
      onEntering={props.onOpening}
      onExit={props.onBeforeClose}
      disableEnforceFocus
      onKeyDown={handleKeyDown}
    >
      <DialogBox
        styles={styles}
        height={height}
        width={width}
        onResize={handleResize}
        windowWidth={windowWidth}
        windowHeight={windowHeight}
        fullscreen={fullScreen}
        dialogMargin={dialogMargin}
        onResizeStart={props.onResizeStart}
        onResizeStop={handleResizeStop}
        responsive={props.isResponsive}
        isResizeDisabled={props.isResizeDisabled}
      >
        {!props.hideTitle && (
          <Title
            {...props.dialogTitle}
            onClose={props.onClose}
            draggableId={draggableId.current}
            onFullScreen={handleFullScreen}
            isFullScreen={fullScreen}
            disableFullScreenAction={props.disableFullScreenAction}
          >
            {props.titleComponent}
          </Title>
        )}
        <DialogContent
          dividers={props.showDividers}
          ref={mergeRefs([dialogContentRef, contentRef])}
          className={classNames(props.contentClassName, 'dp-dialog-content')}
          style={props.contentStyles}
        >
          {props.children}
        </DialogContent>
        <DialogActions {...props.dialogActions}>{props.actionComponent}</DialogActions>
      </DialogBox>
    </MuiDialog>
  );
});

export const Dialog = withStyles(dialogStyles)(DpDialog);
